import { FloatingActionButtons } from "@/components/ui/FloatingActionButtons";
import { EventNotificationModal } from "@/features/events";
import { useEventNotifications } from "@/hooks/useEventNotifications";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { MdCreditCard, MdEmail, MdPhone } from "react-icons/md";
import { useNavigate } from "react-router-dom";
import { loginUser } from "../api/modules";
import { NariShaktiLogo } from "../assets";
import { ROUTES } from "../constants/routes";
import { useAppContext } from "../contexts/AppContext";
import { LoginMethod } from "../types/auth";

const Login = () => {
  const { t } = useTranslation();
  const { refreshUser } = useAppContext();
  const [loginMethod, setLoginMethod] = useState<LoginMethod>(
    LoginMethod.EMAIL
  );
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  // Event notifications for login screen
  const { activeModalEvent, isModalOpen, closeModal } = useEventNotifications();

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm({
    defaultValues: {
      email: "",
      phone: "",
      aadhar: "",
      password: "",
    },
  });

  const onSubmit = async (data: {
    email: string;
    phone: string;
    aadhar: string;
    password: string;
  }) => {
    setError("");
    setLoading(true);
    let identifier = "";
    if (loginMethod === "email") identifier = data.email.trim();
    if (loginMethod === "phone") identifier = data.phone.trim();
    if (loginMethod === "aadhar") identifier = data.aadhar.trim();
    try {
      const res = await loginUser({
        identifier,
        password: data.password.trim(),
        loginMethod,
      });
      if (res.success && res.data) {
        // Handle new API response structure: data.data or direct data
        const responseData = res.data;
        const accessToken =
          responseData?.data?.access_token ||
          responseData?.access_token ||
          responseData?.data?.accessToken ||
          responseData?.accessToken;
        const refreshToken =
          responseData?.data?.refresh_token ||
          responseData?.refresh_token ||
          responseData?.data?.refreshToken ||
          responseData?.refreshToken;

        console.log("📦 Login response structure:", {
          hasData: !!responseData,
          hasNestedData: !!responseData?.data,
          hasAccessToken: !!accessToken,
        });

        if (accessToken) {
          localStorage.setItem("token", accessToken);
          if (refreshToken) {
            localStorage.setItem("refreshToken", refreshToken);
          }
          reset();
          setError("");
          // Refresh user context to update the UI state
          await refreshUser();
          navigate(ROUTES.DASHBOARD);
        } else {
          setError("No access token received. Please try again.");
        }
      } else {
        setError(
          res.message ||
            t("auth.login.loginFailed") ||
            "Login failed. Please check your credentials."
        );
      }
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : t("auth.login.loginFailed") ||
              "Login failed. Please check your credentials."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center registration-container p-3">
      <div className="relative w-full max-w-sm bg-[var(--bg-surface)] rounded-2xl shadow-lg p-6 border border-[var(--border-light)] flex flex-col justify-center">
        {/* Compact Nari Suraksha Logo */}
        <div className="absolute -top-10 left-1/2 -translate-x-1/2 z-20 flex items-center justify-center">
          <div
            className="w-20 h-20 rounded-full border-3 border-[var(--border-medium)] bg-[var(--bg-card)] flex items-center justify-center shadow-lg ring-2 ring-[var(--border-light)]"
            aria-label="Nari Suraksha Logo"
          >
            <img
              src={NariShaktiLogo}
              alt={t("common.nariShaktiLogoAlt", {
                defaultValue: "Nari Suraksha Logo",
              })}
              className="w-16 h-16 object-contain rounded-full"
              loading="eager"
            />
          </div>
        </div>

        {/* Compact Main Content */}
        <div className="flex-1 flex flex-col justify-center mt-6">
          <div className="text-center mb-6">
            <h1 className="text-2xl font-bold text-primary mb-1 tracking-tight">
              {t("auth.login.login") || "लॉगिन"}
            </h1>
            <p className="text-[var(--text-secondary)] text-xs">
              {t("auth.login.welcomeMessage") ||
                "सुरक्षित लॉगिन के लिए अपनी जानकारी दर्ज करें"}
            </p>
          </div>

          <form
            className="flex flex-col gap-4"
            onSubmit={handleSubmit(onSubmit)}
          >
            {/* Compact Login Method Selection */}
            <fieldset className="mb-1">
              <legend className="sr-only">
                {t("auth.login.selectLoginMethod") || "Select login method"}
              </legend>
              <div className="flex gap-2 mb-4 justify-center">
                {[
                  {
                    key: "email",
                    icon: <MdEmail size={16} />,
                    label: t("auth.login.email") || "ईमेल",
                  },
                  {
                    key: "phone",
                    icon: <MdPhone size={16} />,
                    label: t("auth.login.phone") || "फ़ोन",
                  },
                  {
                    key: "aadhar",
                    icon: <MdCreditCard size={16} />,
                    label: t("auth.login.aadhar") || "आधार",
                  },
                ].map(({ key, icon, label }) => (
                  <button
                    type="button"
                    key={key}
                    className={`px-3 py-2 rounded-lg border-2 font-medium transition-all duration-300 min-w-[80px] flex flex-col items-center justify-center gap-0.5 text-xs ${
                      loginMethod === key
                        ? "bg-primary text-white border-primary shadow-md"
                        : "bg-[var(--bg-card)] text-primary border-primary/60 hover:border-primary hover:shadow-sm"
                    }`}
                    onClick={() => {
                      setLoginMethod(key as LoginMethod);
                      setError("");
                    }}
                  >
                    <span className="mb-0.5">{icon}</span>
                    <span className="font-medium">{label}</span>
                  </button>
                ))}
              </div>
            </fieldset>
            {/* Compact Input Fields */}
            {loginMethod === "email" && (
              <div className="relative">
                <input
                  type="email"
                  {...register("email", {
                    required:
                      t("auth.login.emailRequired") || "Email is required",
                    pattern: {
                      value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                      message:
                        t("auth.login.invalidEmail") || "Invalid email address",
                    },
                  })}
                  placeholder={t("auth.login.emailPlaceholder") || "ईमेल"}
                  className={`w-full px-3 py-3 border-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary text-sm font-medium transition-all duration-300 bg-[var(--bg-surface)] text-[var(--text-primary)] ${
                    errors.email
                      ? "border-red-400 focus:border-red-400 focus:ring-red-100"
                      : "border-[var(--border-light)] hover:border-[var(--border-medium)]"
                  }`}
                  autoComplete="username"
                  onFocus={() => setError("")}
                />
                {errors.email && (
                  <div className="text-red-500 text-xs mt-1 flex items-center gap-1">
                    <svg
                      className="w-3 h-3"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                        clipRule="evenodd"
                      />
                    </svg>
                    {errors.email.message as string}
                  </div>
                )}
              </div>
            )}
            {loginMethod === "phone" && (
              <div className="relative">
                <input
                  type="tel"
                  {...register("phone", {
                    required:
                      t("auth.login.phoneRequired") || "Phone is required",
                    pattern: {
                      value: /^\d{10,15}$/,
                      message:
                        t("auth.login.invalidPhone") || "Invalid phone number",
                    },
                  })}
                  placeholder={t("auth.login.phonePlaceholder") || "फ़ोन नंबर"}
                  className={`w-full px-3 py-3 border-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary text-sm font-medium transition-all duration-300 bg-[var(--bg-surface)] text-[var(--text-primary)] ${
                    errors.phone
                      ? "border-red-400 focus:border-red-400 focus:ring-red-100"
                      : "border-[var(--border-light)] hover:border-[var(--border-medium)]"
                  }`}
                  autoComplete="username"
                  onFocus={() => setError("")}
                />
                {errors.phone && (
                  <div className="text-red-500 text-xs mt-1 flex items-center gap-1">
                    <svg
                      className="w-3 h-3"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                        clipRule="evenodd"
                      />
                    </svg>
                    {errors.phone.message as string}
                  </div>
                )}
              </div>
            )}
            {loginMethod === "aadhar" && (
              <div className="relative">
                <input
                  type="text"
                  {...register("aadhar", {
                    required:
                      t("auth.login.aadhaarRequired") || "Aadhar is required",
                    pattern: {
                      value: /^\d{12}$/,
                      message:
                        t("auth.login.invalidAadhaar") ||
                        "Aadhar must be 12 digits",
                    },
                    maxLength: {
                      value: 12,
                      message:
                        t("auth.login.invalidAadhaar") ||
                        "Aadhar must be 12 digits",
                    },
                  })}
                  placeholder={t("auth.login.aadharPlaceholder") || "आधार नंबर"}
                  maxLength={12}
                  className={`w-full px-3 py-3 border-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary text-sm font-medium transition-all duration-300 bg-[var(--bg-surface)] text-[var(--text-primary)] ${
                    errors.aadhar
                      ? "border-red-400 focus:border-red-400 focus:ring-red-100"
                      : "border-[var(--border-light)] hover:border-[var(--border-medium)]"
                  }`}
                  autoComplete="username"
                  onFocus={() => setError("")}
                />
                {errors.aadhar && (
                  <div className="text-red-500 text-xs mt-1 flex items-center gap-1">
                    <svg
                      className="w-3 h-3"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                        clipRule="evenodd"
                      />
                    </svg>
                    {errors.aadhar.message as string}
                  </div>
                )}
              </div>
            )}

            {/* Compact Password Field */}
            <div className="relative">
              <input
                type="password"
                {...register("password", {
                  required:
                    t("auth.login.passwordRequired") || "Password is required",
                  minLength: {
                    value: 6,
                    message:
                      t("auth.login.passwordMinLength") ||
                      "Password must be at least 6 characters",
                  },
                })}
                placeholder={t("auth.login.passwordPlaceholder") || "पासवर्ड"}
                className={`w-full px-3 py-3 border-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary text-sm font-medium transition-all duration-300 bg-[var(--bg-surface)] text-[var(--text-primary)] ${
                  errors.password
                    ? "border-red-400 focus:border-red-400 focus:ring-red-100"
                    : "border-[var(--border-light)] hover:border-[var(--border-medium)]"
                }`}
                autoComplete="current-password"
                onFocus={() => setError("")}
              />
              {errors.password && (
                <div className="text-red-500 text-xs mt-1 flex items-center gap-1">
                  <svg
                    className="w-3 h-3"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                      clipRule="evenodd"
                    />
                  </svg>
                  {errors.password.message as string}
                </div>
              )}
            </div>

            {/* Compact Global Error */}
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-center gap-2">
                <svg
                  className="w-4 h-4 text-red-500"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                    clipRule="evenodd"
                  />
                </svg>
                <span className="text-red-700 text-sm">{error}</span>
              </div>
            )}

            {/* Compact Submit Button */}
            <button
              type="submit"
              className="w-full px-4 py-3 rounded-lg font-semibold bg-primary text-white hover:bg-pink-700 transition-all duration-300 disabled:opacity-50 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 border-2 border-primary text-sm"
              disabled={loading}
            >
              {loading ? (
                <div className="flex items-center justify-center gap-2">
                  <svg
                    className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  {t("auth.login.loggingIn") || "लॉगिन हो रहा है..."}
                </div>
              ) : (
                <div className="flex items-center justify-center gap-2">
                  {t("auth.login.login") || "लॉगिन करें"}
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1"
                    />
                  </svg>
                </div>
              )}
            </button>

            {/* Forgot Password Link */}
            {/* <div className="text-center mt-3">
              <a
                href="/forgot-password"
                className="text-xs text-primary hover:text-pink-700 font-medium transition-colors"
              >
                {t("auth.login.forgotPasswordLink")}
              </a>
            </div> */}
          </form>

          {/* Compact Registration Link */}
          <div className="mt-4 text-center">
            <div className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-[var(--bg-accent)] border border-[var(--border-light)]">
              <span className="text-xs text-gray-600">
                {t("auth.login.noAccount") || "कोई खाता नहीं है?"}
              </span>
              <a
                href="/register"
                className="text-primary hover:text-pink-600 font-semibold text-xs transition-colors duration-300 flex items-center gap-1"
              >
                {t("auth.login.register") || "रजिस्टर करें"}
                <svg
                  className="w-3 h-3"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5l7 7-7 7"
                  />
                </svg>
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Event Notification Modal */}
      {activeModalEvent && (
        <EventNotificationModal
          event={activeModalEvent}
          isOpen={isModalOpen}
          onClose={closeModal}
        />
      )}

      {/* Floating Action Buttons */}
      <FloatingActionButtons />
    </div>
  );
};

export default Login;
